import { Navigate, Outlet, Route, useLocation, useParams } from "react-router-dom";
import PartnerLoginPage from "../../pages/partner/PartnerLoginPage";
import CasaMapaPartnerDashboardPage from "../../pages/partner/CasaMapaPartnerDashboardPage";
import PartnerLeadsPage from "../../pages/partner/PartnerLeadsPage";
import PartnerProfilePage from "../../pages/partner/PartnerProfilePage";
import PartnerCasaMapaProfilePage from "../../pages/partner/PartnerCasaMapaProfilePage";
import PartnerCasaMapaInventorySettingsPage from "../../pages/partner/PartnerCasaMapaInventorySettingsPage";
import PartnerCasaMapaCrmSettingsPage from "../../pages/partner/PartnerCasaMapaCrmSettingsPage";
import PartnerListingCorrectionsPage from "../../pages/partner/PartnerListingCorrectionsPage";
import PartnerSponsorshipAssetsPage from "../../pages/partner/PartnerSponsorshipAssetsPage";
import PartnerSponsorshipPlacementsPage from "../../pages/partner/PartnerSponsorshipPlacementsPage";
import PartnerCampaignsPage from "../../pages/partner/PartnerCampaignsPage";
import PartnerListingsPage from "../../pages/partner/PartnerListingsPage";
import PartnerSourcesPage from "../../pages/partner/PartnerSourcesPage";
import PartnerAgencyInventoryPage from "../../pages/partner/PartnerAgencyInventoryPage";
import PartnerCasaMapaAgenciesPage from "../../pages/partner/PartnerCasaMapaAgenciesPage";
import PartnerCasaMapaListingsPage from "../../pages/partner/PartnerCasaMapaListingsPage";
import PartnerCasaMapaListingDetailPage from "../../pages/partner/PartnerCasaMapaListingDetailPage";
import PartnerCasaMapaCompsPage from "../../pages/partner/PartnerCasaMapaCompsPage";
import PartnerCasaMapaLeadsPage from "../../pages/partner/PartnerCasaMapaLeadsPage";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { isCasaMapaVendorPartner } from "../../utils/casamapaPartnerPortalTabs";
import PartnerCasaMapaMarketPage from "../../pages/partner/PartnerCasaMapaMarketPage";
import PartnerCasaMapaBriefPage from "../../pages/partner/PartnerCasaMapaBriefPage";
import PartnerCasaMapaHeatmapPage from "../../pages/partner/PartnerCasaMapaHeatmapPage";
import PartnerCasaMapaPulsePage from "../../pages/partner/PartnerCasaMapaPulsePage";
import PartnerCasaMapaDirectoryPage from "../../pages/partner/PartnerCasaMapaDirectoryPage";
import PartnerVendorWorkspaceLayout from "./PartnerVendorWorkspaceLayout";
import { partnerVendorDirectoryRouteElements } from "./PartnerVendorDirectoryRoutes";
import { PartnerPortalBaseProvider, usePartnerPortalBase } from "./PartnerPortalBaseProvider";
import PartnerPortalLayout from "./PartnerPortalLayout";
import PartnerLegacyRedirect from "./PartnerLegacyRedirect";
import { automapAdminPortalPath } from "../../constants/automapAdminPortalPaths";
import { AUTOMAP_CLIENT_VIEW_SLUG } from "../../constants/automapPartnerPortalConfig";
import { CASAMAPA_ADMIN_PORTAL_BASE } from "../../constants/casamapaAdminPortalPaths";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../../constants/casamapaPartnerPortalConfig";
import { CASAMAPA_PARTNER_PORTAL_I18N_MANIFEST } from "../../constants/casamapaPartnerPortalI18n";
import PartnerPortalDashboardPage from "../../pages/partner/PartnerPortalDashboardPage";
import MidpointMediaCustomerDashboardPage from "../../pages/partner/MidpointMediaCustomerDashboardPage";
import MidpointMediaCustomerTicketsPage from "../../pages/partner/MidpointMediaCustomerTicketsPage";
import MidpointMediaCustomerInfrastructurePage from "../../pages/partner/MidpointMediaCustomerInfrastructurePage";
import MidpointMediaCustomerProjectsPage from "../../pages/partner/MidpointMediaCustomerProjectsPage";
import { MIDPOINT_MEDIA_CLIENT_VIEW_SLUG } from "../../constants/partnerPortalConfig";
import { IntlProvider } from "../../intl";

/** Legacy admin2 URLs redirect to internal admin (not partner). */
function CasaMapaAdmin2LegacyRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/v\/casamapa\/admin2\/?/, "");
  const target = suffix
    ? `${CASAMAPA_ADMIN_PORTAL_BASE}/${suffix}${location.search}${location.hash}`
    : `${CASAMAPA_ADMIN_PORTAL_BASE}/dashboard${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

function ClientViewPartnerPortalShell() {
  const { clientViewSlug } = useParams();

  const shell = (
    <PartnerPortalBaseProvider clientViewSlug={clientViewSlug}>
      <Outlet />
    </PartnerPortalBaseProvider>
  );

  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return (
      <IntlProvider surface="clientView" manifest={CASAMAPA_PARTNER_PORTAL_I18N_MANIFEST}>
        {shell}
      </IntlProvider>
    );
  }

  return shell;
}

function PartnerPortalHomeRedirect() {
  const { dashboardPath } = usePartnerPortalBase();
  return <Navigate to={dashboardPath} replace />;
}

function PartnerDashboardRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <CasaMapaPartnerDashboardPage />;
  }
  // AutoMapa ops live under /v/automap/admin — keep legacy /partner dashboard redirects.
  if (clientViewSlug === AUTOMAP_CLIENT_VIEW_SLUG) {
    return <Navigate to={automapAdminPortalPath("dashboard")} replace />;
  }
  if (clientViewSlug === MIDPOINT_MEDIA_CLIENT_VIEW_SLUG) {
    return <MidpointMediaCustomerDashboardPage />;
  }
  // Other Client View partner portals stay on /v/:slug/partner.
  return <PartnerPortalDashboardPage />;
}

function MidpointMediaOnlyRoute({ children }) {
  const { clientViewSlug, path } = usePartnerPortalBase();
  if (clientViewSlug !== MIDPOINT_MEDIA_CLIENT_VIEW_SLUG) {
    return <Navigate to={path("dashboard")} replace />;
  }
  return children;
}

function PartnerListingDetailRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaListingDetailPage />;
  }
  return <Navigate to="../listings" replace />;
}

function PartnerListingCompsRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaCompsPage />;
  }
  return <Navigate to="../listings" replace />;
}

function PartnerListingsRoute() {
  const { clientViewSlug, path } = usePartnerPortalBase();
  const { partner } = usePartnerAuth();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    if (isCasaMapaVendorPartner(partner)) {
      return <Navigate to={path("dashboard")} replace />;
    }
    return <PartnerCasaMapaListingsPage />;
  }
  return <PartnerListingsPage />;
}

function PartnerLeadsRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaLeadsPage />;
  }
  return <PartnerLeadsPage />;
}

function PartnerProfileRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaProfilePage />;
  }
  return <PartnerProfilePage />;
}

function PartnerInventorySettingsRoute() {
  const { clientViewSlug, path } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaInventorySettingsPage />;
  }
  return <Navigate to={path("profile")} replace />;
}

function PartnerCrmSettingsRoute() {
  const { clientViewSlug, path } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaCrmSettingsPage />;
  }
  return <Navigate to={path("profile")} replace />;
}

function PartnerAgenciesRoute() {
  const { clientViewSlug } = usePartnerPortalBase();
  if (clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG) {
    return <PartnerCasaMapaAgenciesPage />;
  }
  return <Navigate to="dashboard" replace />;
}

function PartnerToolsLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`../market${location.hash}`} replace />;
}

/** Former per-agency /portal workspace — keep bookmarks from 404ing. */
function AgencyPortalRemovedRedirect() {
  const { agencySlug } = useParams();
  const location = useLocation();
  const { path } = usePartnerPortalBase();
  if (/\/portal\/inventory\/?$/.test(location.pathname)) {
    return <Navigate to={path(`agencies/${agencySlug}/inventory`)} replace />;
  }
  if (/\/portal\/leads\/?$/.test(location.pathname)) {
    return <Navigate to={path("leads")} replace />;
  }
  return <Navigate to={path("agencies")} replace />;
}

function partnerPortalLayoutChildRoutes() {
  return (
    <>
      <Route path="dashboard" element={<PartnerDashboardRoute />} />
      <Route
        path="tickets"
        element={
          <MidpointMediaOnlyRoute>
            <MidpointMediaCustomerTicketsPage />
          </MidpointMediaOnlyRoute>
        }
      />
      <Route
        path="infrastructure"
        element={
          <MidpointMediaOnlyRoute>
            <MidpointMediaCustomerInfrastructurePage />
          </MidpointMediaOnlyRoute>
        }
      />
      <Route
        path="projects"
        element={
          <MidpointMediaOnlyRoute>
            <MidpointMediaCustomerProjectsPage />
          </MidpointMediaOnlyRoute>
        }
      />
      <Route path="agencies" element={<PartnerAgenciesRoute />} />
      <Route path="agencies/:agencySlug/portal/*" element={<AgencyPortalRemovedRedirect />} />
      <Route path="agencies/:agencySlug/portal" element={<AgencyPortalRemovedRedirect />} />
      <Route path="agencies/:agencySlug/inventory" element={<PartnerAgencyInventoryPage />} />
      <Route path="listings/:listingId/comps" element={<PartnerListingCompsRoute />} />
      <Route path="listings/:listingId" element={<PartnerListingDetailRoute />} />
      <Route path="listings" element={<PartnerListingsRoute />} />
      <Route path="tools" element={<PartnerToolsLegacyRedirect />} />
      <Route path="market/brief" element={<PartnerCasaMapaBriefPage />} />
      <Route path="market/heatmap" element={<PartnerCasaMapaHeatmapPage />} />
      <Route path="market/pulse" element={<PartnerCasaMapaPulsePage />} />
      <Route path="market" element={<PartnerCasaMapaMarketPage />} />
      <Route path="directory" element={<PartnerCasaMapaDirectoryPage />} />
      <Route path="vendors/workspace/:clientViewId" element={<PartnerVendorWorkspaceLayout />}>
        {partnerVendorDirectoryRouteElements()}
      </Route>
      <Route path="leads" element={<PartnerLeadsRoute />} />
      <Route path="profile/inventory" element={<PartnerInventorySettingsRoute />} />
      <Route path="profile/crm" element={<PartnerCrmSettingsRoute />} />
      <Route path="profile" element={<PartnerProfileRoute />} />
      <Route path="listing-corrections" element={<PartnerListingCorrectionsPage />} />
      <Route path="sponsorship-assets" element={<PartnerSponsorshipAssetsPage />} />
      <Route path="sponsorship-placements" element={<PartnerSponsorshipPlacementsPage />} />
      <Route path="campaigns" element={<PartnerCampaignsPage />} />
      <Route path="sources" element={<PartnerSourcesPage />} />
    </>
  );
}

export function ClientViewPartnerPortalRouteGroup() {
  return (
    <>
      <Route path="/partner/login" element={<Navigate to={automapAdminPortalPath("login")} replace />} />
      <Route path="/partner/*" element={<PartnerLegacyRedirect />} />

      <Route path="/v/casamapa/admin2/*" element={<CasaMapaAdmin2LegacyRedirect />} />

      <Route path="/v/:clientViewSlug/partner" element={<ClientViewPartnerPortalShell />}>
        <Route path="login" element={<PartnerLoginPage />} />
        <Route index element={<PartnerPortalHomeRedirect />} />
        <Route element={<PartnerPortalLayout />}>
          {partnerPortalLayoutChildRoutes()}
        </Route>
      </Route>
    </>
  );
}
